import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { useToast } from '../../hooks/use-toast';
import { 
  Settings,
  Save,
  RotateCcw,
  FileText,
  Shield,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const AdminSettings = () => {
  const [promptTemplate, setPromptTemplate] = useState('');
  const [isModified, setIsModified] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  // Default prompt template
  const DEFAULT_PROMPT = `Generate a Lawful remedy by doing the following:

1. Draft a Testimony under God with the Defendant's name and Conditional Acceptance asserting an Affidavit of Status, English Common Law Rights, Constitutional Protections and Maxims of Equity while demanding a full accounting, and require all obligations to be settled in honor; also include a fee schedule for any possible Violations.

2. An overview of the English Common Law and Equitable violations that were involved in the court case matter.

3. Explain All the steps of the Administrative Process to put the Opposing party in Administrative Default.

4. Recommend sending all mailings via Registered Mail or Certified Mail with a green return slip.

5. Resources for information on Common Law and Equity Jurisprudence.

Please structure your response with clear sections for each of the above requirements.`;

  useEffect(() => {
    loadPromptTemplate();
  }, []);

  const loadPromptTemplate = () => {
    const savedPrompt = localStorage.getItem('remedy_prompt_template');
    const currentPrompt = savedPrompt || DEFAULT_PROMPT;
    setPromptTemplate(currentPrompt);
    setIsModified(false);
  };

  const handlePromptChange = (value) => {
    setPromptTemplate(value);
    setIsModified(value !== (localStorage.getItem('remedy_prompt_template') || DEFAULT_PROMPT));
  };

  const savePromptTemplate = async () => {
    setIsSaving(true);
    
    try {
      // Save to localStorage (in production, this would be an API call)
      localStorage.setItem('remedy_prompt_template', promptTemplate);
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('promptTemplateUpdated', { 
        detail: { promptTemplate } 
      }));
      
      setIsModified(false);
      
      toast({
        title: "Prompt Template Saved",
        description: "The remedy generator will now use your updated prompt template.",
      });
      
    } catch (error) {
      console.error('Failed to save prompt template:', error);
      toast({
        title: "Error",
        description: "Failed to save prompt template. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const resetToDefault = () => {
    setPromptTemplate(DEFAULT_PROMPT);
    setIsModified(true);
    toast({
      title: "Reset to Default",
      description: "Prompt template has been reset. Click Save to apply changes.",
    });
  };

  // Check if user has admin access (you might want to implement proper admin role checking)
  const isAdmin = user?.email?.includes('admin') || user?.subscription_plan === 'defender_of_the_people' || user?.subscription_plan === 'lifetime' || user?.role === 'admin';

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
            <p className="text-gray-600">
              Admin access required to modify system settings.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50 overflow-hidden">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Settings className="w-6 h-6" />
                Admin Settings
              </h1>
              <p className="text-gray-600 mt-1">
                Configure system-wide settings and templates
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isModified && (
                <div className="flex items-center gap-1 text-orange-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Unsaved changes
                </div>
              )}
            </div>
          </div>

          {/* Prompt Template Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                AI Remedy Generator Prompt Template
              </CardTitle>
              <p className="text-sm text-gray-600">
                This prompt template is used by the AI when generating legal remedies in the Workflow Builder. 
                Modify it to customize the structure and content of AI responses.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prompt Template
                </label>
                <Textarea
                  value={promptTemplate}
                  onChange={(e) => handlePromptChange(e.target.value)}
                  placeholder="Enter your prompt template..."
                  className="min-h-[400px] font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Available variables: The system will automatically prepend "Legal Category: [CATEGORY]" and "Case Description: [USER_DESCRIPTION]" before your template.
                </p>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Button
                    onClick={savePromptTemplate}
                    disabled={!isModified || isSaving}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isSaving ? (
                      <>
                        <Settings className="mr-2 w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 w-4 h-4" />
                        Save Template
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={resetToDefault}
                    variant="outline"
                    className="border-gray-300"
                  >
                    <RotateCcw className="mr-2 w-4 h-4" />
                    Reset to Default
                  </Button>
                </div>

                <div className="text-sm text-gray-500">
                  {!isModified && (
                    <div className="flex items-center gap-1 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      Template saved
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Template Preview
              </CardTitle>
              <p className="text-sm text-gray-600">
                This is how your prompt template will appear to the AI (with example data).
              </p>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 p-4 rounded-lg border">
                <div className="font-mono text-sm whitespace-pre-wrap">
                  <span className="text-blue-600 font-semibold">Legal Category: TRAFFIC</span>
                  <br /><br />
                  <span className="text-blue-600 font-semibold">Case Description: I received a traffic citation for speeding but believe the officer did not have proper jurisdiction.</span>
                  <br /><br />
                  <span className="text-gray-700">{promptTemplate}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Usage Instructions */}
          <Card>
            <CardHeader>
              <CardTitle>Usage Instructions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm text-gray-600 space-y-2">
                <p><strong>How it works:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Users select a legal category and describe their case in the Workflow Builder</li>
                  <li>The system combines their input with your prompt template</li>
                  <li>The complete prompt is sent to the AI for analysis</li>
                  <li>The AI generates a structured response based on your template</li>
                  <li>Users can then save the response to their Document Vault</li>
                </ul>
                <p className="mt-3"><strong>Best Practices:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Be specific about the structure you want in responses</li>
                  <li>Include clear instructions for each section</li>
                  <li>Test changes by generating sample remedies</li>
                  <li>Save backup copies of working templates before making major changes</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;